// Copy and view rules for the Buyer Insights Products tab (spec §2). Pure functions; unit-tested.
import { formatDate, formatRange } from './buyerInsightsDates';

export const VIEW_SIDE = {
  all: 'both',
  still_wanted: 'sent',
  discount_timing: 'both',
  only_you: 'received',
  never_bought: 'sent',
};

export const SORT_FALLBACK = {
  received_clicks: 'sent_clicks',
  received_orders: 'sent_orders',
  received_revenue: 'sent_orders',
  sent_clicks: 'received_clicks',
  sent_orders: 'received_orders',
  sent_matches: 'received_clicks',
};

// A sort column of the hidden side falls back to the same metric on the visible side.
export function resolveSort(sort, side) {
  if (side === 'both' || sort.startsWith(side)) return sort;
  return SORT_FALLBACK[sort] || (side === 'sent' ? 'sent_clicks' : 'received_clicks');
}

const COLUMN_LABELS = {
  received_clicks: 'Received clicks',
  received_orders: 'Received orders',
  received_revenue: 'Received revenue',
  sent_matches: 'Sent matches shown',
  sent_clicks: 'Sent clicks',
  sent_orders: 'Sent orders',
};

// `revenue` false drops Received revenue (Sizes only you have: its numbers are window-scoped).
export function sortOptions(side, { revenue = true } = {}) {
  const keys = [];
  if (side !== 'sent') keys.push('received_clicks', 'received_orders', ...(revenue ? ['received_revenue'] : []));
  if (side !== 'received') keys.push('sent_matches', 'sent_clicks', 'sent_orders');
  return keys.map((value) => ({ value, label: COLUMN_LABELS[value] }));
}

// The filter part of every Buyer Insights query (Products and Brands): omit empties, brand /
// category as repeated params, live dates only when the mode is complete.
export function filterParams({ window, myRetailerId, brand, category, status, live, sale, q, soldOutAt, offRange, bands }) {
  const params = { start: window.start, end: window.end };
  if (myRetailerId) params.my_retailer_id = myRetailerId;
  const brands = Array.isArray(brand) ? brand : brand ? [brand] : [];
  const categories = Array.isArray(category) ? category : category ? [category] : [];
  if (brands.length) params.brand = brands;
  if (categories.length) params.category = categories;
  if (status && status !== 'all') params.status = status;
  if (sale && sale !== 'all') params.sale = sale;
  // "Sold out at" (sold-out-at spec §5.1): the bucket, its % off range, and the retailer's cut points
  if (soldOutAt && soldOutAt !== 'all') {
    params.sold_out_at = soldOutAt;
    if (soldOutAt === 'discounted' && offRange) {
      if (offRange.min !== null && offRange.min !== undefined) params.min_off = offRange.min;
      if (offRange.max !== null && offRange.max !== undefined) params.max_off = offRange.max;
    }
  }
  if (Array.isArray(bands) && bands.length) params.bands = bands.join(',');
  if (q) params.q = q;
  if (live && live.mode && live.mode !== 'any') {
    const complete =
      (live.mode === 'between' && live.from && live.to) ||
      (live.mode === 'after' && live.from) ||
      (live.mode === 'before' && live.to);
    if (complete) {
      params.live_mode = live.mode;
      if (live.mode !== 'before') params.live_from = live.from;
      if (live.mode !== 'after') params.live_to = live.to;
    }
  }
  return params;
}

// Query params for GET /buyer-insights/products: the filters plus view, sort (on the visible
// side), n only for still wanted and group only for discount timing.
export function buildProductsParams({
  window, myRetailerId, view, brand, category, status, live, sale, q, n, sort, group, soldOutAt, offRange, bands,
}) {
  const params = {
    ...filterParams({ window, myRetailerId, brand, category, status, live, sale, q, soldOutAt, offRange, bands }),
    view,
    sort: resolveSort(sort, VIEW_SIDE[view] || 'both'),
  };
  if (view === 'still_wanted' && n) params.n = n;
  if (view === 'discount_timing' && group) params.group = group;
  return params;
}

// Query params for GET /buyer-insights/brands. Brands you don't carry is about partners' brands:
// Brand, Product status, Product live date, Sale and Sold out at do not apply there (spec §1.5).
export function buildBrandsParams({ window, myRetailerId, view, brand, category, status, live, sale, q, sort, soldOutAt, offRange, bands }) {
  const notCarried = view === 'not_carried';
  return {
    ...filterParams({
      window,
      myRetailerId,
      category,
      q,
      brand: notCarried ? [] : brand,
      status: notCarried ? 'all' : status,
      live: notCarried ? null : live,
      sale: notCarried ? 'all' : sale,
      soldOutAt: notCarried ? 'all' : soldOutAt,
      offRange: notCarried ? null : offRange,
      bands: notCarried ? [] : bands,
    }),
    view,
    ...(notCarried ? {} : { sort }),
  };
}

// ---- "Sold out at" (sold-out-at spec §2.2, §4): the retailer's cut points in words, as the server labels them.

// (20, 40) → below 20% off · 20% to 39% off · 40% off and more; [] → discounted
export function bandLabels(bands) {
  if (!bands || !bands.length) return ['discounted'];
  const labels = [`below ${bands[0]}% off`];
  for (let i = 1; i < bands.length; i += 1) {
    const lo = bands[i - 1];
    const hi = bands[i] - 1;
    labels.push(hi === lo ? `${lo}% off` : `${lo}% to ${hi}% off`);
  }
  labels.push(`${bands[bands.length - 1]}% off and more`);
  return labels;
}

// The {min, max} of bucket i (null = open end), as the API takes them.
export function bucketRange(i, bands) {
  return { min: i > 0 ? bands[i - 1] : null, max: i < bands.length ? bands[i] - 1 : null };
}

// Which bucket a range selects, or -1 when it is not one of the bands' buckets.
export function bandIndexOf(offRange, bands) {
  if (!offRange || !bands || !bands.length) return -1;
  for (let i = 0; i <= bands.length; i += 1) {
    const r = bucketRange(i, bands);
    if (r.min === offRange.min && r.max === offRange.max) return i;
  }
  return -1;
}

// "Sold out at: full price" / "20% to 39% off" — the filter in words for empty states and tracking.
export function soldOutAtLabel(soldOutAt, offRange) {
  if (!soldOutAt || soldOutAt === 'all') return '';
  if (soldOutAt === 'full_price') return 'full price';
  if (soldOutAt !== 'discounted' || !offRange || (offRange.min == null && offRange.max == null)) return 'discounted';
  if (offRange.min == null) return `below ${offRange.max + 1}% off`;
  if (offRange.max == null) return `${offRange.min}% off and more`;
  return offRange.min === offRange.max ? `${offRange.min}% off` : `${offRange.min}% to ${offRange.max}% off`;
}

// The size table cell: "Full price · 3 Sep" / "35% off · 12 Jul" / "Unknown" / "—" (in stock)
export function soldOutAtText(sa) {
  if (!sa) return '—';
  const day = sa.day ? ` · ${formatDate(sa.day)}` : '';
  if (sa.state === 'discounted') return `${sa.pct_off != null ? `${sa.pct_off}% off` : 'Discounted'}${day}`;
  if (sa.state === 'full_price') return `Full price${day}`;
  return 'Unknown';
}

// "6.4 weeks before your sale (YAYA sale from 28 Jun)" / "no sale for this brand yet" for a full-price sell-out
export function weeksBeforeSaleText(row, brand) {
  if (!row || !row.sold_out_at || row.sold_out_at.state !== 'full_price') return '';
  if (row.weeks_before_sale == null || !row.your_sale_start) return 'no sale for this brand yet';
  const who = brand ? `${brand} sale` : 'sale';
  return `${row.weeks_before_sale} weeks before your sale (${who} from ${formatDate(row.your_sale_start)})`;
}

export const fmtEur = (n) =>
  n === null || n === undefined ? '—' : `€${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
export const fmtInt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-GB'));
const plural = (n, one, many = `${one}s`) => `${fmtInt(n)} ${n === 1 ? one : many}`;

// Narrow layout: "Received 41 clicks · 3 orders" / "Sent 1,204 shown · 86 clicks · 5 orders"
export function formatStatLine(side, numbers) {
  if (!numbers) return '';
  if (side === 'received') {
    const revenue = numbers.revenue !== undefined && numbers.revenue !== null ? ` · ${fmtEur(numbers.revenue)} revenue` : '';
    return `Received ${plural(numbers.clicks, 'click')} · ${plural(numbers.orders, 'order')}${revenue}`;
  }
  return `Sent ${fmtInt(numbers.matches_shown)} shown · ${plural(numbers.clicks, 'click')} · ${plural(numbers.orders, 'order')}`;
}

export const SIZE_STATUS_LABEL = {
  in_stock: 'In stock',
  sold_out: 'Sold out',
  only_you: 'Only you have it in stock',
  never_bought: 'Never bought in',
};

export function sizeStatusLabel(status) {
  return SIZE_STATUS_LABEL[status] || status;
}

// "{Status} since {date}[ · only you {window}] · status on {End}[ (after the period: sold out {date})]"
export function formatSizeChipTooltip(size, end) {
  const parts = [sizeStatusLabel(size.status) + (size.since ? ` since ${formatDate(size.since)}` : '')];
  if (size.sole_window) {
    parts.push(
      size.sole_window.to
        ? `only you ${formatRange(size.sole_window.from, size.sole_window.to)}`
        : `only you since ${formatDate(size.sole_window.from)}`
    );
  }
  parts.push(`status on ${formatDate(end)}`);
  let text = parts.join(' · ');
  if (size.after_end) {
    text += ` (after the period: ${sizeStatusLabel(size.after_end.status).toLowerCase()} ${formatDate(size.after_end.date)})`;
  }
  return text;
}

export function liveFilterSummary(live) {
  if (!live || !live.mode || live.mode === 'any') return '';
  if (live.mode === 'after' && live.from) return `Products live since ${formatDate(live.from)}`;
  if (live.mode === 'before' && live.to) return `Products live before ${formatDate(live.to)}`;
  if (live.mode === 'between' && live.from && live.to) {
    return `Products live between ${formatDate(live.from)} and ${formatDate(live.to)}`;
  }
  return '';
}

// Discount timing with markdowns in the period, but none in the selected group.
const TIMING_GROUP_EMPTY = {
  you_first: 'No product where you discounted first in this period',
  partners_first: 'No product where a partner discounted before you in this period',
  same_week: 'No product where you and a partner discounted in the same week in this period',
  only_you: 'No product where only you discounted in this period',
};

// "Try Sale: All." / "Try Sold out at: All." / "Try Sale: All or Sold out at: All." (sold-out-at spec §4)
const SALE_HINT = (sale, soldOutAt = 'all') => {
  const parts = [];
  if (sale && sale !== 'all') parts.push('Sale: All');
  if (soldOutAt && soldOutAt !== 'all') parts.push('Sold out at: All');
  return parts.length ? ` Try ${parts.join(' or ')}.` : '';
};

export function emptyStateText(view, { n = 14, q = '', hasFilters = false, group = '', sale = 'all', soldOutAt = 'all' } = {}) {
  if (q) return `No products match “${q}”.`;
  // a markdown day is never a full-price day: the view is empty by definition (sale-filter spec §3.2)
  if (view === 'discount_timing' && sale === 'full_price') {
    return 'Your markdowns are not shown under Full price only. Set Sale to All or On sale to see them.';
  }
  if (view === 'discount_timing' && TIMING_GROUP_EMPTY[group]) {
    return `${TIMING_GROUP_EMPTY[group]}${hasFilters ? ' for these filters' : ''}.`;
  }
  const base = {
    all: 'No products were matched with a partner in this period',
    still_wanted: `No sizes sold out for ${n}+ days in this period with matches shown since`,
    never_bought: 'No inserted sizes got matches in this period',
    only_you: 'No sizes were only in stock with you and got shoppers in this period',
    discount_timing: 'No markdowns in this period',
  }[view];
  // the filter is ignored on views that are not sell-outs, so its hint would mislead there
  const soa = ['all', 'still_wanted'].includes(view) ? soldOutAt : 'all';
  return `${base}${hasFilters ? ' for these filters' : ''}.${SALE_HINT(sale, soa)}`;
}

const SALE_FILE_SUFFIX = { full_price: '-full-price', on_sale: '-on-sale' };
export const csvFilename = (view, start, end, sale = 'all') =>
  `products-${view}-${start}_${end}${SALE_FILE_SUFFIX[sale] || ''}.csv`;

// ---- Timeline (spec §3)

const bandRange = (band, end) => (band.to ? formatRange(band.from, band.to) : `${formatDate(band.from)} – ${formatDate(end)}`);

// "30% off, 15 Jul – 25 Sep" / "Full price, 27 Jun – 14 Jul"
export function formatPriceBandTooltip(band, end) {
  const what = band.discounted ? (band.pct_off ? `${band.pct_off}% off` : 'On sale') : 'Full price';
  return `${what}, ${bandRange(band, end)}`;
}

// "Sold out 2 Jul – 25 Sep" / "Available 27 Jun – 1 Jul"
export function formatRunTooltip(run, end) {
  return `${run.in_stock ? 'Available' : 'Sold out'} ${bandRange(run, end)}`;
}

// "4 Aug: 12 matches shown · 2 clicks · 1 order"
export function formatDemandTooltip(d) {
  return `${formatDate(d.day)}: ${fmtInt(d.matches_shown)} matches shown · ${plural(d.clicks, 'click')} · ${plural(d.orders, 'order')}`;
}

export const EVENT_COLORS = {
  first_seen: '#757575',
  first_matched: '#1976d2',
  sold_out: '#616161',
  back_in_stock: '#1976d2',
  discounted: '#ef6c00',
  full_price: '#757575',
  partner_sold_out: '#ef6c00',
  partner_back_in_stock: '#1976d2',
  partner_order: '#c62828',
};

export const eventDotColor = (kind) => EVENT_COLORS[kind] || '#9e9e9e';

export const VIEW_DESCRIPTIONS = {
  all: 'Every product HandOff matched with a partner in this period.',
  still_wanted: null, // rendered with the inline N picker
  never_bought: 'Sizes partners carry that were inserted into your size container because you never bought them. Sizes that sold out before HandOff first saw the product can appear here.',
  only_you: 'Sizes only you had in stock at times in this period. Partners sent you shoppers for them.',
  discount_timing: 'Your first markdown day, compared with partners who carry the same product.',
};

export const oneSidedNote = (side) =>
  side === 'sent' ? 'Sent only in this view' : side === 'received' ? 'Received only in this view' : '';

// ---- Brands tab (spec §1)

export const BRAND_VIEW_DESCRIPTIONS = {
  all: 'Every brand you carry that was matched with a partner in this period.',
  thinner_sizes: 'Brands where you were sold out of at least one size for more of the period than partners.',
  thinner_colours: 'Brands where you were sold out of at least one colour for more of the period than partners.',
  send_more: 'Brands where you sent partners more clicks than you received.',
  receive_more: 'Brands where partners sent you more clicks than you sent them.',
  not_carried: "Brands your partners got clicks for in this period that you don't stock.",
};

export function brandEmptyText(view, { q = '', hasFilters = false, category = false, sale = 'all', soldOutAt = 'all' } = {}) {
  if (q) return `No brands match “${q}”.`;
  if (view === 'not_carried') {
    return category
      ? "No brands you don't carry got partner clicks in this category."
      : "No brands you don't carry got partner clicks in this period.";
  }
  const base = {
    all: 'No brands got clicks in this period',
    thinner_sizes: 'No brands where you were thinner than partners on a size in this period',
    thinner_colours: 'No brands where you were thinner than partners on a colour in this period',
    send_more: 'No brands where you sent partners more clicks in this period',
    receive_more: 'No brands where partners sent you more clicks in this period',
  }[view];
  return `${base}${hasFilters ? ' for these filters' : ''}.${SALE_HINT(sale, soldOutAt)}`;
}

export const balanceLabel = (b) => `${fmtInt(b.received)} received · ${fmtInt(b.sent)} sent`;

// "68 sizes in 36 products still wanted" — a size is one product × size, so name both counts.
const sizesInProducts = (sizes, products) =>
  `${fmtInt(sizes)} ${sizes === 1 ? 'size' : 'sizes'}` +
  (products ? ` in ${fmtInt(products)} ${products === 1 ? 'product' : 'products'}` : '');

export const stillWantedSignal = (sizes, products) => `${sizesInProducts(sizes, products)} still wanted`;

export const stillWantedLine = (count, n = 14, products = 0) =>
  count > 0
    ? `${sizesInProducts(count, products)} sold out for ${n}+ days in this period, with matches shown since.`
    : `No sizes sold out for ${n}+ days in this period, with matches shown since.`;

export const cohortLine = (cohort) =>
  cohort && cohort.length
    ? `Compared with the partners you share the most clicks with: ${cohort.map((c) => c.name).join(', ')}`
    : '';

// "Thinner in 3 sizes · 2 colours" (only the parts that are not zero); '' when neither.
export function thinnerSignal(t) {
  if (!t) return '';
  const parts = [];
  if (t.sizes) parts.push(`${fmtInt(t.sizes)} ${t.sizes === 1 ? 'size' : 'sizes'}`);
  if (t.colours) parts.push(`${fmtInt(t.colours)} ${t.colours === 1 ? 'colour' : 'colours'}`);
  return parts.length ? `Thinner in ${parts.join(' · ')}` : '';
}

// normalized colours read "purple_light" → "Purple light".
export const colourLabel = (c) => {
  const s = (c || '').replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

const BASE_COLOURS = {
  black: '#212121', white: '#fafafa', grey: '#9e9e9e', gray: '#9e9e9e', silver: '#bdbdbd', blue: '#1e88e5',
  navy: '#283593', red: '#e53935', pink: '#ec407a', purple: '#8e24aa', green: '#43a047', olive: '#827717',
  yellow: '#fdd835', orange: '#fb8c00', brown: '#795548', beige: '#d7ccc8', camel: '#c19a6b', gold: '#d4af37',
  cream: '#fff8e1', khaki: '#c3b091', multi: 'linear-gradient(90deg, #e53935, #fdd835, #1e88e5)',
};
const SHADE = { light: 0.35, dark: -0.3 };

const mix = (hex, amount) => {
  if (!hex.startsWith('#')) return hex;
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) =>
    Math.round(amount > 0 ? v + (255 - v) * amount : v * (1 + amount))
  );
  return `rgb(${ch.join(', ')})`;
};

// A swatch for a normalized colour: base word ("blue_navy" → navy wins) lightened / darkened.
export function colourSwatch(c) {
  const words = (c || '').toLowerCase().split('_');
  const base = [...words].reverse().find((w) => BASE_COLOURS[w]) || words.find((w) => BASE_COLOURS[w]);
  const hex = BASE_COLOURS[base] || '#bdbdbd';
  const shade = words.find((w) => SHADE[w] !== undefined);
  return shade ? mix(hex, SHADE[shade]) : hex;
}

// Share of days sold out in plain words (Daniel 2026-10-05): "about 6 days in 10".
export function daysInTen(pct) {
  if (pct === null || pct === undefined) return '—';
  if (pct < 5) return 'less than 1 day in 10';
  if (pct >= 100) return 'every day';
  if (pct >= 95) return 'almost every day';
  const n = Math.round(pct / 10);
  return `about ${n} ${n === 1 ? 'day' : 'days'} in 10`;
}

// Row tooltip (spec §1.4) with the exact shares: "Size 44, 6 Jul – 4 Oct, on days you and your
// partners both had these styles online: you were sold out 58% of the time, partners 41% (you're
// thinner by 17 pts)".
export function depthTooltip(kind, row, window) {
  const what = kind === 'size' ? `Size ${row.label}` : colourLabel(row.label);
  const diff = row.diff_pts;
  const tail = row.thinner
    ? `you're thinner by ${diff} pts`
    : diff < 0
      ? `you have more depth by ${-diff} pts`
      : 'in line with partners';
  return `${what}, ${formatRange(window.start, window.end)}, on days you and your partners both had these styles online: you were sold out ${row.you_pct}% of the time, partners ${row.partners_pct}% (${tail})`;
}
